// services/dateTicks.js

/**
 * Generate tick positions (timestamps) for Highcharts X-axis
 * @param {string[]} dates - Array of date strings (e.g. ["2025-09-01", "2025-09-02"])
 * @returns {number[]} - Array of timestamps to be used as tickPositions
 */

export function generateDateTicks(dates) {
  if (!dates || dates.length === 0) return [];
  const timestamps = dates.map(d => new Date(d).getTime());
  const startDate = Math.min(...timestamps);
  const endDate = Math.max(...timestamps);
  const rangeDays = (endDate - startDate) / (1000 * 60 * 60 * 24);
  const oneDay = 24 * 3600 * 1000;

  let tickPositions = [];

  // Always include the first dates
  tickPositions.push(startDate);

  if (rangeDays <= 7) {
    // ✅ Case 1: ≤ 7 days — show all days
    for (let d = startDate + oneDay; d <= endDate; d += oneDay) {
      tickPositions.push(d);
    }
  } 
  else if (rangeDays < 30) {
    // ✅ Case 2: < 30 days — 7-day interval + first + last
    for (let d = startDate + 7 * oneDay; d < endDate; d += 7 * oneDay) {
      tickPositions.push(d);
    }

    // Check the gap between the last tick and the end date
    const lastTick = tickPositions[tickPositions.length - 1];
    const gap = (endDate - lastTick) / oneDay;

    // If gap < 7 days, remove last tick to avoid overlap
    if (gap < 1) {
      tickPositions.pop();
    }

    // Always add the final date
    tickPositions.push(endDate);
  } 
  else {
    // ✅ Case 3: ≥ 30 days — month-wise ticks + start + end
    const start = new Date(startDate);
    const end = new Date(endDate);

    const current = new Date(start.getFullYear(), start.getMonth() + 1, 1);
    while (current < end) {
      tickPositions.push(current.getTime());
      current.setMonth(current.getMonth() + 1);
    }

    // Same logic: avoid overlap if last tick too close
    const lastTick = tickPositions[tickPositions.length - 1];
    const gap = (endDate - lastTick) / oneDay;

    if (gap < 7) {
      tickPositions.pop();
    }

    // Always add the final date
    tickPositions.push(endDate);
  }

  // Ensure unique and sorted ticks
  tickPositions = [...new Set(tickPositions)].sort((a, b) => a - b);

  return tickPositions;
}


